// ppt-gen-client.ppt.service.ts
import { Injectable } from "@angular/core";
import pptxgen from "pptxgenjs";
import { saveAs } from 'file-saver';
import { EventDetailsFormValues, VendorSubmissionFormValues } from './ppt-gen.common.service'; 

type ImgRect = { x: number; y: number; w: number; h: number };

@Injectable({
    providedIn: 'root'
})
export class ClientPptGenService {

    private pageTemplate = {
        coverPage: null, thankYouPage: null, submissionPages: [], submissionPicturePages: [],
    }

    constructor() {
        this.loadTemplates();
    }

    /**
     * Converts an image URL to a Base64 string.
     * @param url The URL of the image.
     * @returns A Promise that resolves with the Base64 string of the image.
     */
    getBase64FromUrl(url: string): Promise<string> {
        return fetch(url)
            .then(response => response.blob())
            .then(blob => new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            }));
    }

    /**
     * Loads the background image templates for the PPT slides.
     * These images are converted to Base64 for embedding in the PPTX.
     */
    async loadTemplates() {
        try {
            this.pageTemplate.coverPage = await this.getBase64FromUrl('assets/ppt-theme-1/Slide1.PNG');
            const subPage1 = await this.getBase64FromUrl('assets/ppt-theme-1/Slide2.PNG');
            const picPage1 = await this.getBase64FromUrl('assets/ppt-theme-1/Slide3.PNG');
            const subPage2 = await this.getBase64FromUrl('assets/ppt-theme-1/Slide4.PNG');
            const picPage2 = await this.getBase64FromUrl('assets/ppt-theme-1/Slide5.PNG');
            this.pageTemplate.thankYouPage = await this.getBase64FromUrl('assets/ppt-theme-1/Slide6.PNG');
            this.pageTemplate.submissionPages = [subPage1, subPage2];
            this.pageTemplate.submissionPicturePages = [picPage1, picPage2];
        } catch (error) {
            console.error("Failed to load PPT templates:", error);
            // Handle error, e.g., use default slides or inform the user
        }
    }

    /**
     * Generates a client-facing PPTX file with event details, vendor submissions, and a thank you page.
     * @param eventDetails Details about the event for the cover and thank you pages.
     * @param vendorSubmissions Data containing selected vendor answers.
     */
    async generateVendorSubmissionClientPPT(
        eventDetails: EventDetailsFormValues,
        vendorSubmissions: { vendors: VendorSubmissionFormValues[] }
    ) {
        // console.log("Generating Client PPT...");
        // console.log("Event Details:", eventDetails);
        // console.log("Vendor Submissions:", vendorSubmissions);

        let pres = new pptxgen();

        // --- Global Slide Layout Settings ---
        const SLIDE_MARGIN_X = 3.5;
        const SLIDE_MARGIN_Y = 0.5;
        const MAX_USABLE_SLIDE_HEIGHT = 6.5; // Usable height for content
        const QUESTION_FONT_SIZE = 14; // Slightly larger for client presentation
        const ANSWER_FONT_SIZE = 12;
        // Approximate height per line in inches for estimation.
        // autoFit will handle exact wrapping, so this is a rough guide.
        const LINE_HEIGHT_FACTOR = 0.18; 
        const SPACE_BETWEEN_QA_PAIRS = 0.3; // Space after each combined Q&A block within the single text box

        // --- 1. Front Page (Cover Page) ---
        if (eventDetails.includeCoverPage) {
            let coverSlide = pres.addSlide();
            if (this.pageTemplate.coverPage) {
                coverSlide.background = { data: this.pageTemplate.coverPage };
            }
            coverSlide.addText(eventDetails.eventName, {
                x: 0.5, y: 3.7, w: '50%', align: 'left',
                fontSize: 30, bold: true, color: "FFFFFF"
            });
            coverSlide.addText(eventDetails.eventStartDate || '', {
                x: 0.5, y: 4.7, w: '50%', align: 'left',
                fontSize: 15, bold: false, color: "FFFFFF"
            });
        }

        // --- 2. Vendor Specific Slides ---
        for (const vendor of vendorSubmissions.vendors) {
            const selectedAnswers = vendor.answers.filter(answer => answer.selected);

            // If a vendor has no selected answers, skip them
            if (selectedAnswers.length === 0 && vendor.photos.length === 0) {
                // console.log(`Skipping vendor ${vendor.vendorName} as they have no selected answers or photos.`);
                continue;
            }

            // Add answer slides if there are selected answers
            if (selectedAnswers.length > 0) {
                let currentAnswerIndex = 0;
                while (currentAnswerIndex < selectedAnswers.length) {
                    let slide = pres.addSlide();
                    if (this.pageTemplate.submissionPages.length > 0) {
                        slide.background = { 
                            data: this.pageTemplate.submissionPages[
                                Math.floor(Math.random() * this.pageTemplate.submissionPages.length)
                            ]
                        }; 
                    }
                    
                    // Vendor Name as Title
                    const titleYPos = SLIDE_MARGIN_Y;
                    const contentStartYPos = titleYPos + 0.8; // Space after vendor title
                    slide.addText(`${vendor.vendorName}`, {
                        x: SLIDE_MARGIN_X,
                        y: titleYPos,
                        fontSize: 32,
                        bold: true,
                        color: "96E6E6", 
                        w: 6,
                        h: 0.6
                    });

                    let slideTextRuns: pptxgen.TextProps[] = [];
                    let estimatedCurrentSlideHeight = 0;
                    let answersAddedToCurrentSlide = 0;

                    // Loop to collect answers for the current slide
                    for (let i = currentAnswerIndex; i < selectedAnswers.length; i++) {
                        const answer = selectedAnswers[i];
                        const questionText = `${answer.questionText}: `; 
                        const answerText = answer.answerText;

                        // Estimate height for this specific Q&A pair
                        const estimatedQuestionLines = Math.ceil(questionText.length / (70 * (QUESTION_FONT_SIZE / 12))) || 1;
                        const estimatedAnswerLines = Math.ceil(answerText.length / (80 * (ANSWER_FONT_SIZE / 12))) || 1;

                        const estimatedHeightForThisQAPair = 
                            (estimatedQuestionLines * LINE_HEIGHT_FACTOR * (QUESTION_FONT_SIZE / ANSWER_FONT_SIZE)) +
                            (estimatedAnswerLines * LINE_HEIGHT_FACTOR) +
                            SPACE_BETWEEN_QA_PAIRS;

                        // Check if adding this content would exceed slide height
                        if (estimatedCurrentSlideHeight + estimatedHeightForThisQAPair > (MAX_USABLE_SLIDE_HEIGHT - contentStartYPos) && answersAddedToCurrentSlide > 0) {
                            break; 
                        }

                        // Add question and answer text runs with bullet points
                        slideTextRuns.push(
                            { text: "• ", options: { fontSize: ANSWER_FONT_SIZE, color: "FFFFFF" } }, // Bullet point
                            { text: questionText, options: { bold: true, fontSize: QUESTION_FONT_SIZE, color: "96E6E6" } },
                            { text: answerText, options: { fontSize: ANSWER_FONT_SIZE, color: "FFFFFF" } },
                            { text: '\n\n', options: { fontSize: 1 } } // Add a small vertical space between Q&A pairs
                        );
                        
                        estimatedCurrentSlideHeight += estimatedHeightForThisQAPair;
                        answersAddedToCurrentSlide++;
                        currentAnswerIndex++; // Move to the next answer for the next iteration
                    }

                    // Add all collected text runs for the current slide into a single text box
                    if (slideTextRuns.length > 0) {
                        slide.addText(
                            slideTextRuns,
                            {
                                x: SLIDE_MARGIN_X, 
                                y: contentStartYPos,
                                w: 6, 
                                h: MAX_USABLE_SLIDE_HEIGHT - 2, // Allow full remaining height for autoFit
                                autoFit: true, 
                                valign: 'top',
                                lineSpacing: 18 // Adjust line spacing for better readability within the single box
                            }
                        );
                    }
                }
            }

            // --- Photo Slides ---
            const selectedPhotos = vendor.photos.filter(photo => photo.selected);

            if (selectedPhotos.length > 0) {
                let photoSlide = pres.addSlide();
                if (this.pageTemplate.submissionPicturePages.length > 0) {
                    photoSlide.background = {
                        data: this.pageTemplate.submissionPicturePages[
                            Math.floor(Math.random() * this.pageTemplate.submissionPicturePages.length)
                        ]
                    };
                }

                photoSlide.addText(`${vendor.sectionHeading}`, {
                    x: 1.0,
                    y: SLIDE_MARGIN_Y,
                    fontSize: 32,
                    bold: true,
                    color: "3D008E",
                    w: 9.4,
                    h: 0.6,
                    align: 'center'
                });

                const IMG_LEFT = 1.05;
                const IMG_CONTENT_Y = 1.55;
                const IMG_RIGHT_EDGE = 10.15;
                const IMG_BOTTOM_EDGE = 6.15;
                const usableW = IMG_RIGHT_EDGE - IMG_LEFT;
                const usableH = IMG_BOTTOM_EDGE - IMG_CONTENT_Y;
                const CONTENT_SAFE_RIGHT = 0.6; // leave space for rounded border
                const CONTENT_SAFE_LEFT = 0.25; // additional left gutter
                const CONTENT_SAFE_BOTTOM = 0.3; // bottom padding
                const GRID_GAP = 0.18;
                const safeHorizSpace = usableW - (CONTENT_SAFE_LEFT + CONTENT_SAFE_RIGHT);
                const contentWidth = Math.max(3, Math.min(safeHorizSpace, 7.5));
                const horizontalCenterOffset = Math.max(0, safeHorizSpace - contentWidth) / 2;
                const contentX = IMG_LEFT + CONTENT_SAFE_LEFT + horizontalCenterOffset;
                const contentY = IMG_CONTENT_Y;
                const contentHeight = usableH - CONTENT_SAFE_BOTTOM;
                const contentArea = {
                    x: contentX,
                    y: contentY,
                    width: contentWidth,
                    height: contentHeight
                };

                const photosToDisplay = selectedPhotos.slice(0, 4);
                const photoCount = photosToDisplay.length;
                const layouts = this.buildPhotoLayouts(photoCount, contentArea, GRID_GAP);

                for (let i = 0; i < photoCount; i++) {
                    const photo = photosToDisplay[i];
                    try {
                        if (!photo.base64Content) {
                            let photoU = photo.fileUrl;
                            if (!photoU.includes('https')) {
                                photoU = photoU.replace('http', 'https');
                            }
                            photo.base64Content = await this.getBase64FromUrl(photoU);
                        }
                        if (photo.base64Content) {
                            const layout = layouts[i];
                            if (!layout) {
                                continue;
                            }
                            photoSlide.addImage({
                                data: photo.base64Content,
                                x: layout.x,
                                y: layout.y,
                                w: layout.w,
                                h: layout.h,
                            });
                        }
                    } catch (error) {
                        console.error(`Failed to load image for ${photo.fileName}:`, error);
                    }
                }
            }
        }

        // --- 3. Thank You Page ---
        if (eventDetails.includeThankYouPage) {
            let thankYouSlide = pres.addSlide();
            if (this.pageTemplate.thankYouPage) {
                thankYouSlide.background = { data: this.pageTemplate.thankYouPage };
            };
        }

        // --- Save the PPTX file ---
        const fileName = `${eventDetails.eventName.replace(/[^a-zA-Z0-9]/g, '_')}_Vendor_Overview.pptx`;
        pres.writeFile({ fileName: fileName });
        console.log("Client PPT Generation Complete.");
    }

    private buildPhotoLayouts(
        photoCount: number,
        area: { x: number; y: number; width: number; height: number },
        gap: number
    ): ImgRect[] {
        if (photoCount === 0) {
            return [];
        }

        const { rows, cols } = this.getPhotoGridDimensions(photoCount);
        const totalHorizontalGaps = cols + 1;
        const totalVerticalGaps = rows + 1;
        const cellWidth = (area.width - totalHorizontalGaps * gap) / cols;
        const cellHeight = (area.height - totalVerticalGaps * gap) / rows;
        const layouts: ImgRect[] = [];

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                layouts.push({
                    x: area.x + gap + col * (cellWidth + gap),
                    y: area.y + gap + row * (cellHeight + gap),
                    w: cellWidth,
                    h: cellHeight,
                });
            }
        }

        return layouts;
    }

    private getPhotoGridDimensions(photoCount: number): { rows: number; cols: number } {
        if (photoCount === 1) {
            return { rows: 1, cols: 1 };
        }
        if (photoCount === 2) {
            return { rows: 1, cols: 2 };
        }
        return { rows: 2, cols: 2 };
    }
}
